import React from "react";
import PropTypes from "prop-types";
import { Alert, Box, Typography, Chip } from "@mui/material";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GlassCard from "../../../components/common/GlassCard";

function getSeverityColor(severity) {
  switch (severity) {
    case "critical":
      return "error";
    case "high":
      return "warning";
    case "medium":
      return "info";
    default:
      return "success";
  }
}

function SocialDeterminantsCard({ socialData }) {
  const hasBarriers = socialData && socialData.barriers_identified > 0;

  return (
    <GlassCard
      title="Social Determinants of Health"
      icon={<HealthAndSafetyIcon color="primary" />}
      isAI
    >
      {hasBarriers ? (
        socialData.findings.map((finding, index) => (
          <Alert
            key={`${finding.domain}-${index}`}
            severity={getSeverityColor(finding.severity)}
            sx={{
              mb: 1,
              borderRadius: 2,
              background:
                "linear-gradient(135deg, rgba(138, 43, 226, 0.08) 0%, rgba(0, 191, 255, 0.08) 100%)",
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {finding.domain}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {finding.barrier}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
              <CheckCircleIcon sx={{ color: "success.main", fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: "success.main" }}>
                Intervention: {finding.intervention}
              </Typography>
            </Box>
            {finding.all_interventions && finding.all_interventions.length > 1 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" fontWeight={600} display="block">
                  Additional Resources:
                </Typography>
                {finding.all_interventions.slice(1).map((intervention, idx) => (
                  <Typography key={idx} variant="caption" display="block" sx={{ ml: 1 }}>
                    • {intervention}
                  </Typography>
                ))}
              </Box>
            )}
            {finding.evidence && (
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 0.5, fontStyle: "italic", color: "text.secondary" }}
              >
                Evidence: {finding.evidence}
              </Typography>
            )}
            {finding.tags && finding.tags.length > 0 && (
              <Box mt={1} display="flex" gap={0.5} flexWrap="wrap">
                {finding.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
            )}
          </Alert>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          {socialData?.status === "unavailable"
            ? socialData.message || "AI check is unavailable right now."
            : socialData ? "No social determinants barriers identified" : "Loading SDOH assessment..."}
        </Typography>
      )}
    </GlassCard>
  );
}

SocialDeterminantsCard.propTypes = {
  socialData: PropTypes.shape({
    status: PropTypes.string,
    message: PropTypes.string,
    barriers_identified: PropTypes.number,
    findings: PropTypes.arrayOf(
      PropTypes.shape({
        domain: PropTypes.string,
        barrier: PropTypes.string,
        severity: PropTypes.string,
        intervention: PropTypes.string,
        all_interventions: PropTypes.arrayOf(PropTypes.string),
        evidence: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
      })
    ),
  }),
};

SocialDeterminantsCard.defaultProps = {
  socialData: null,
};

export default SocialDeterminantsCard;
